/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Formik, Form } from "formik";
import {
  BreadcrumbComponent,
  CardComponent,
  CustomTabPanelComponent,
  DescriptionInputComponent,
  AlertComponent,
  ConfirmationPopup,
} from "../../../../common/components";
import { leadCreate } from "../../../../api/lead";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { initialValues, validationSchema } from "./create-lead-helper";
import CreateLeadPrimaryDetails from "./create-lead-primary-details/create-lead-primary-details";
import CreateLeadAdditionalDetails from "./create-lead-additional-details/create-lead-additional-details";
import CreateLeadAddressDetails from "./create-lead-address-details/create-lead-address-details";
import descriptionIcon from "../../../../assets/icons/description.svg";
import "./create-lead.scss";
import { useLeadOwnerOptions } from "../../../../utils/getLeadOwnerOptions";
import AlertIcon from "../../../../assets/icons/alert-icon.svg";
 
const CreateLead = (props: any) => {
  const {
    createLeadInitialValues,
    setCreateLeadInitialValues,
    setIsShowCreateLeadSection,
    updateLeadList,
    setAlertOpen,
    setAlertMessage,
    setAlertType,
  } = props;
 
  const breadcrumbLink = [
    { label: "Lead Management", link: "#", handleClick: () => { } },
    {
      label: "Leads",
      link: "/home/lead-management/lead-details",
      handleClick: () => {
        setIsShowCreateLeadSection(false);
        setLoading(false);
        setCreateLeadInitialValues(initialValues);
        setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
        setSelectedLeadSource("Manual");
        setSelectedLeadStatus("New Lead");
        setSelectedRating("");
        setMaritalStatus(null);
        setInterestedProduct([]);
        setPreviewImage(false);
      },
    },
    { label: "Create Leads", link: "#", handleClick: () => { } },
  ];
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions();
  const [selectedLeadOwner, setSelectedLeadOwner] = useState("");
  useEffect(() => {
    if (LEAD_OWNER_OPTIONS?.length)
      setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
  }, [LEAD_OWNER_OPTIONS]);
  const [selectedInterestedProduct, setInterestedProduct] = useState(createLeadInitialValues?.interestedProduct ? [createLeadInitialValues?.interestedProduct] : []);
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState("Manual");
  const [selectedLeadStatus, setSelectedLeadStatus] = useState("New Lead");
  const [selectedRating, setSelectedRating] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isPreviewImage, setPreviewImage] = useState(false);
  const [indexValue, setIndexValue] = React.useState(0);
  const [isNavigate, setIsNavigate] = useState(false);
  const [showClosedWonConfirm, setShowClosedWonConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | { values: any; resetForm: any; }>(null);
  const navigateRef = React.useRef(true);
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
 
  const handleCancel = async () => {
    setCreateLeadInitialValues(initialValues);
    setIsShowCreateLeadSection(false);
  };
  const handlePopup = async (values: any, resetForm: any) => {
    if (selectedLeadStatus === "Closed-Won") {
      // Pause before API call, ask for confirmation
      setPendingAction({ values, resetForm });
      setShowClosedWonConfirm(true);
    } else {
      // Save directly
      await handleSave(values, resetForm);
    }
  };
  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (selectedLeadStatus === "Closed-Won") {
        setShowClosedWonConfirm(true);
      }
 
      const leadDetails = {
        company: values.company,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        primaryEmail: values.email,
        secondaryEmail: values.secondaryEmail || null,
        primaryPhoneNumber: values?.primaryPhoneNumber || null,
        secondaryPhoneNumber: values?.secondaryPhoneNumber || null,
        website: values.website || null,
        leadSource: selectedLeadSource || null,
        leadStatus: selectedLeadStatus || null,
        faxNumber: values.fax || null,
        industry: values.industry || null,
        numberOfEmployees: values.numEmployees
          ? Number(values.numEmployees)
          : null,
        annualRevenue: values.annualRevenue || null,
        interestedProducts:
          Array.isArray(selectedInterestedProduct) &&
            selectedInterestedProduct.length > 0
            ? selectedInterestedProduct.join(", ")
            : null,
        confidenceScore: selectedRating || null,
        socialLeadId: values.socialLeadId || null,
        description: values.description || null,
        designation: values.designation || null,
        middleName: values.middleName || null,
        age: parseInt(values.age || 0) || null,
        leadValue: values.leadValue || null,
        maritalStatus: maritalStatus || null,
        relationship: values.relationship || null,
        contactPerson: values.contactPerson || null,
        contactPersonPhoneNumber: values.contactPersonPhoneNumber || null,
        deathDate: values.deathDate
          ? values.deathDate.format("YYYY-MM-DD")
          : null,
      };
 
      Object.entries(leadDetails).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
 
      [
        "streetLine1",
        "streetLine2",
        "city",
        "state",
        "country",
        "zipcode",
      ].forEach((field) => {
        if (values[field]) {
          formData.append(
            `address[${field.replace("Address", "")}]`,
            values[field]
          );
        }
      });
      if (values.imageUrl instanceof File) {
        formData.append("leadImg", values.imageUrl);
      }
 
      const response = await leadCreate(formData);
 
      if (response.data.success) {
        setLoading(false);
        setCreateLeadInitialValues(initialValues);
        setAlertMessage("Lead saved successfully!");
        setAlertType("success");
        setAlertOpen(true);
        setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0]?.value);
        setSelectedLeadSource("Manual");
        setSelectedLeadStatus("New Lead");
        setSelectedRating("");
        setMaritalStatus(null);
        setInterestedProduct([]);
        setPreviewImage(false);
        setShowClosedWonConfirm(false);
        setTimeout(() => {
          if (isNavigate) {
            setIsNavigate(false);
            setIsShowCreateLeadSection(false);
          }
          setCreateLeadInitialValues(initialValues);
          resetForm();
          updateLeadList();
        }, 100);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
        "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
 
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
 
  const handleMainTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setIndexValue(newValue);
  };
  return (
    <>
      <div className="leadcreate-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={breadcrumbLink}
            showBackArrow={true}
            handleBackArrowClick={() => {
              setCreateLeadInitialValues(initialValues);
              setIsShowCreateLeadSection(false);
            }}
          />
        </div>
        <div className="lead-create-filter-wrapper">
          <Formik
            initialValues={createLeadInitialValues || initialValues}
            validationSchema={validationSchema(false)}
            enableReinitialize={true}
            onSubmit={(values, { resetForm }) => handlePopup(values, resetForm)}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              validateField,
              setFieldTouched,
              touched,
              errors,
              submitCount,
              resetForm
            }) => (
              <Form>
                <div className="lead-create-tab-section">
                  <div className="lead-create-tab-name-section">
                    <Box>
                      <Tabs
                        value={indexValue}
                        onChange={handleMainTabChange}
                        allowScrollButtonsMobile
                      >
                        <Tab label="Primary Details" />
                        <Tab label="Additional Details" />
                        <Tab label="Address Information" />
                      </Tabs>
                    </Box>
                  </div>
                  <div className="lead-create-tab-data-section">
                    <CustomTabPanelComponent value={indexValue} index={0}>
                      <CreateLeadPrimaryDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadOwner={setSelectedLeadOwner}
                        selectedLeadOwner={selectedLeadOwner}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        setSelectedLeadStatus={setSelectedLeadStatus}
                        selectedLeadStatus={selectedLeadStatus}
                        setSelectedRating={setSelectedRating}
                        selectedRating={selectedRating}
                        setInterestedProduct={setInterestedProduct}
                        selectedInterestedProduct={selectedInterestedProduct}
                        setPreviewImage={setPreviewImage}
                        isPreviewImage={isPreviewImage}
                        validateField={validateField}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                    </CustomTabPanelComponent>
                    <CustomTabPanelComponent value={indexValue} index={1}>
                      <CreateLeadAdditionalDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadOwner={setSelectedLeadOwner}
                        selectedLeadOwner={selectedLeadOwner}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        setSelectedLeadStatus={setSelectedLeadStatus}
                        selectedLeadStatus={selectedLeadStatus}
                        setSelectedRating={setSelectedRating}
                        selectedRating={selectedRating}
                        setInterestedProduct={setInterestedProduct}
                        selectedInterestedProduct={selectedInterestedProduct}
                        maritalStatus={maritalStatus}
                        setMaritalStatus={setMaritalStatus}
                        touched={touched}
                        validateField={validateField}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                      />
                    </CustomTabPanelComponent>
                    <CustomTabPanelComponent value={indexValue} index={2}>
                      <CreateLeadAddressDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadOwner={setSelectedLeadOwner}
                        selectedLeadOwner={selectedLeadOwner}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        setSelectedLeadStatus={setSelectedLeadStatus}
                        selectedLeadStatus={selectedLeadStatus}
                        setSelectedRating={setSelectedRating}
                        selectedRating={selectedRating}
                      />
                    </CustomTabPanelComponent>
                  </div>
                </div>
                <div className="lead-form-wrapper">
                  <CardComponent title="Description" icon={descriptionIcon}>
                    <div className="form-grid-three">
                      <DescriptionInputComponent
                        fieldName="description"
                        label="Company Description"
                        placeholder="Add Description"
                        optional={false}
                        disabled={false}
                        value={values.description}
                        onChange={(e: any) =>
                          setFieldValue("description", e?.target?.value)
                        }
                        maxlength={150}
                      />
                    </div>
                  </CardComponent>
                </div>
                <Box className="footer">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={async () => {
                      setIsNavigate(false);
                      handleSubmit();
 
                    }}
                  >
                    Save & New
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      setIsNavigate(true);
                      handleSubmit();
 
                    }}
                  >
                    Save
                  </button>
                </Box>
                <ConfirmationPopup
                  title="Convert Lead to Customer"
                  open={showClosedWonConfirm}
                  note="Are you sure you want to convert this lead into a customer? All lead information will be transferred to the customer record"
                  onCancel={() => {
                    setShowClosedWonConfirm(false);
                    setPendingAction(null);
                  }}
                  onOkay={() => {
                    setShowClosedWonConfirm(false);
                    if (pendingAction) {
                      handleSave(pendingAction.values, pendingAction.resetForm);
                      setPendingAction(null);
                    }
                  }}
                  buttonText="Confirm"
                  Icon={AlertIcon}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};
 
export default CreateLead;
